import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { UserContext } from '@/components/user/provider'
import { DIGITANE_VIDEO_SITE_ID } from '../../digitaneVideo/config'
import type {
  DigitaneVideoEventHandlers,
  DigitaneVideoInitOptions,
} from '../../digitaneVideo/types'
import {
  DigitaneVideoStatus,
  useDigitaneVideo,
} from '../../digitaneVideo/useDigitaneVideo'
import { useProject } from '../../project/hook'
import { findChapterIndexBySeq, getCurrentChapterSeq } from '../chapterSeq'
import { navigateToDigitaneTown } from '../town'

interface UseMissionVideoWidgetOptions {
  /** Widget の iframe を入れる要素 */
  readonly targetRef: RefObject<HTMLDivElement | null>
  /** true になったら autoplay 付きで Widget を作り直す（チュートリアル終了時） */
  readonly startVideo: boolean
  /** Widget が窓の高さを通知した（window:resized） */
  readonly onResized: (height: number) => void
  /** Widget の最小化ボタンが押された（window:minimized） */
  readonly onMinimized: () => void
}

interface UseMissionVideoWidgetResult {
  readonly status: DigitaneVideoStatus
  readonly error: Error | null
  /** 読み込み（embed.js の取得・init）に失敗した Widget を作り直す */
  readonly retry: () => void
}

/** Widget を初期化するときの状態。generation が変わると作り直す */
interface InitState {
  readonly generation: number
  readonly autoplay: boolean
  readonly chapterSeq: number | undefined
}

/**
 * ミッションの動画窓（digitane 動画 Widget の floating モード）の設定組み立てと
 * イベント配線。
 *
 * - 開始チャプターは provider が決めた missionManager.chapterIndex の seq を渡す
 *   （URL で指定されたチャプター -> 最初の未視聴 -> 先頭。チュートリアルは index 1）
 * - Widget でチャプターが変わったら missionManager.chapterIndex を合わせる
 *   （Blockly のツールボックスが切り替わる）
 * - クリアの記録は Widget が行う。robcratch は視聴済み情報を取り直すだけ
 */
export const useMissionVideoWidget = ({
  targetRef,
  startVideo,
  onResized,
  onMinimized,
}: UseMissionVideoWidgetOptions): UseMissionVideoWidgetResult => {
  const { missionInfo, missionManager, updateChapters, loading } = useProject()
  const { isOnlineUser } = useContext(UserContext)

  // missionInfo オブジェクトは updateChapters() のたびに作り直されるため、
  // effect の依存には ID だけを使う
  const missionId = missionInfo?.id
  const ready = !loading && missionId !== undefined

  // missionManager は変更のたびに Proxy が作り直される。effect や非同期処理からは
  // 常に最新のものを読む
  const missionManagerRef = useRef(missionManager)
  missionManagerRef.current = missionManager

  // 初期化状態は遅延して決める。シェルのマウント時点ではまだ provider が
  // チャプターを設定していない可能性があるため、ready になってから現在の
  // チャプターの seq を読む
  const [initState, setInitState] = useState<InitState | null>(null)
  const lastMissionIdRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!ready) {
      setInitState(null)
      return
    }

    // ミッションが変わったときは、前のミッションの状態を持ち越さず決め直す
    const isMissionChanged = lastMissionIdRef.current !== missionId
    lastMissionIdRef.current = missionId
    setInitState((prev) =>
      isMissionChanged || prev === null ?
        {
          generation: 0,
          autoplay: false,
          chapterSeq: getCurrentChapterSeq(missionManagerRef.current),
        }
      : prev,
    )
  }, [ready, missionId])

  // 現在の Widget（generation）が再生を始めたか。利用者が案内中に Widget の
  // 再生ボタンを押していたら、チュートリアル終了時に作り直さない
  const playedInGenerationRef = useRef(false)
  // Widget が最後に通知したチャプターの seq
  const lastWidgetChapterSeqRef = useRef<number | undefined>(undefined)
  const widgetGenerationRef = useRef(0)

  // Widget を作り直す（初期化・破棄・autoplay での再初期化）たびに、
  // 前の Widget の情報を捨てる
  useEffect(() => {
    widgetGenerationRef.current += 1
    playedInGenerationRef.current = false
    lastWidgetChapterSeqRef.current = undefined
    return () => {
      // 破棄した窓の取り直しが後から完了しても、別の作品の位置を変えない。
      widgetGenerationRef.current += 1
    }
  }, [initState, missionId, isOnlineUser])

  // チュートリアル終了（startVideo が false -> true）で、現在のチャプターから
  // 自動再生で作り直す。親から再生を始める API は無いため、init し直す
  const prevStartVideoRef = useRef(startVideo)
  useEffect(() => {
    const wasStarted = prevStartVideoRef.current
    prevStartVideoRef.current = startVideo
    if (wasStarted || !startVideo) return

    // 案内中に利用者が再生していたら、再生中の動画を頭から読み直さない
    if (playedInGenerationRef.current) return

    setInitState(
      (state) =>
        state && {
          generation: state.generation + 1,
          autoplay: true,
          chapterSeq: getCurrentChapterSeq(missionManagerRef.current),
        },
    )
  }, [startVideo])

  // 読み込みに失敗したときの再試行。同じ設定（開始チャプター・autoplay）のまま
  // init し直す。embed.js の読み込みは、失敗した後の呼び出しで読み込み直される
  const retry = useCallback(() => {
    setInitState(
      (state) => state && { ...state, generation: state.generation + 1 },
    )
  }, [])

  const options = useMemo<DigitaneVideoInitOptions | null>(() => {
    if (initState === null || missionId === undefined) return null

    return {
      missionId,
      mode: 'floating',
      siteId: DIGITANE_VIDEO_SITE_ID,
      isOnlineUser,
      autoplay: initState.autoplay,
      autoResize: false,
      ...(typeof initState.chapterSeq === 'number' ?
        { chapterSeq: initState.chapterSeq }
      : {}),
    }
  }, [initState, missionId, isOnlineUser])

  // onResized / onMinimized は親が毎回新しい関数を渡しても、最新のものを呼ぶ
  const onResizedRef = useRef(onResized)
  onResizedRef.current = onResized
  const onMinimizedRef = useRef(onMinimized)
  onMinimizedRef.current = onMinimized

  /** missionManager のチャプターを、Widget が表示しているチャプターに合わせる */
  const syncToWidgetChapter = (): void => {
    const manager = missionManagerRef.current
    const index = findChapterIndexBySeq(
      manager.chapters,
      lastWidgetChapterSeqRef.current,
    )
    // seq で見つからなければ何もしない
    if (index !== undefined && index !== manager.chapterIndex) {
      manager.chapterIndex = index
    }
  }

  const markPlayed = (): void => {
    playedInGenerationRef.current = true
  }

  const handlers: DigitaneVideoEventHandlers = {
    'window:resized': (event) => {
      const { height } = event.payload
      if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
        onResizedRef.current(height)
      }
    },
    'window:minimized': () => {
      onMinimizedRef.current()
    },
    'video:started': markPlayed,
    'video:resumed': markPlayed,
    'chapter:changed': (event) => {
      lastWidgetChapterSeqRef.current = event.payload.chapterSeq
      syncToWidgetChapter()
    },
    // Widget がクリアを記録した後に届く。視聴済み情報を取り直す。
    // updateChapters() は chapters を入れ替える直前（取得後）の chapterIndex を
    // 書き戻すため、取得中に利用者が Widget でチャプターを変えても、その位置は
    // 保たれる。取得後の syncToWidgetChapter は、念のため Widget が最後に通知した
    // チャプターへ合わせ直す処理。
    // Promise.resolve は、Promise を返さない実装（テストのスタブなど）でも
    // イベントハンドラの中で例外にしないため
    'chapter:cleared': () => {
      const generation = widgetGenerationRef.current
      void Promise.resolve(updateChapters()).then(() => {
        if (generation === widgetGenerationRef.current) syncToWidgetChapter()
      })
    },
    'navigate:town': () => {
      navigateToDigitaneTown()
    },
  }

  const { status, error } = useDigitaneVideo({
    targetRef,
    options,
    handlers,
    generation: initState?.generation ?? 0,
  })

  return { status, error, retry }
}
